/** Plain polygon geometry on local metres (spec §4.1). Rings are simple, counter-clockwise, open. */
import { rotate } from './frame';
import type { XY } from './types';

const EPS = 1e-9;

function signedArea(poly: XY[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

/** Drop a repeated closing vertex and orient counter-clockwise. */
export function ring(poly: XY[]): XY[] {
  let out = poly;
  if (out.length > 1) {
    const first = out[0];
    const last = out[out.length - 1];
    if (Math.abs(first.x - last.x) < EPS && Math.abs(first.y - last.y) < EPS) out = out.slice(0, -1);
  }
  return signedArea(out) < 0 ? [...out].reverse() : [...out];
}

export function area(poly: XY[]): number {
  return Math.abs(signedArea(poly));
}

export function bounds(poly: XY[]): { minX: number; maxX: number; minY: number; maxY: number } {
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

/**
 * Direction of the flight lines that makes them fewest and longest: the edge direction with the
 * smallest polygon extent perpendicular to it (rotating calipers over edge directions).
 * Returns the math angle in degrees, [0, 180), counter-clockwise from +x.
 */
export function autoAngle(poly: XY[]): number {
  let best = 0;
  let bestWidth = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (Math.hypot(b.x - a.x, b.y - a.y) < EPS) continue;
    let deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    deg = ((deg % 180) + 180) % 180;
    const rotated = poly.map((p) => rotate(p, -deg));
    const { minY, maxY } = bounds(rotated);
    const width = maxY - minY;
    if (width < bestWidth - EPS || (Math.abs(width - bestWidth) <= EPS && deg < best)) {
      best = deg;
      bestWidth = width;
    }
  }
  return Math.abs(best - 180) < EPS ? 0 : best;
}

/**
 * Outward offset by `d` metres (mitred; a mitre longer than 3·d is bevelled, parallel edges are
 * joined by the plain offset point). The input must be counter-clockwise (see `ring`).
 */
export function buffer(poly: XY[], d: number): XY[] {
  if (d <= 0 || poly.length < 3) return [...poly];
  const n = poly.length;
  const normals = poly.map((a, i) => {
    const b = poly[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    // Interior is on the left of a counter-clockwise edge, so outward is the right-hand normal.
    return { x: (b.y - a.y) / len, y: -(b.x - a.x) / len };
  });
  const out: XY[] = [];
  for (let i = 0; i < n; i++) {
    const p = poly[i];
    const nPrev = normals[(i - 1 + n) % n];
    const nCur = normals[i];
    const prev = poly[(i - 1 + n) % n];
    const next = poly[(i + 1) % n];
    // Offset lines: through p+nPrev·d along (p−prev) and through p+nCur·d along (next−p).
    const p1 = { x: p.x + nPrev.x * d, y: p.y + nPrev.y * d };
    const d1 = { x: p.x - prev.x, y: p.y - prev.y };
    const p2 = { x: p.x + nCur.x * d, y: p.y + nCur.y * d };
    const d2 = { x: next.x - p.x, y: next.y - p.y };
    const cross = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(cross) < EPS) {
      out.push(p2);
      continue;
    }
    const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / cross;
    const m = { x: p1.x + d1.x * t, y: p1.y + d1.y * t };
    if (Math.hypot(m.x - p.x, m.y - p.y) > 3 * d) {
      out.push(p1, p2);
    } else {
      out.push(m);
    }
  }
  return out;
}

export interface Rect {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** Ray casting; a point on the boundary may land either way, which the callers tolerate. */
export function pointInPolygon(poly: XY[], p: XY): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y)) inside = !inside;
  }
  return inside;
}

function orient(a: XY, b: XY, c: XY): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** Proper or touching intersection of segments ab and cd. */
export function segmentsIntersect(a: XY, b: XY, c: XY, d: XY): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 * o2 < 0 && o3 * o4 < 0) return true;
  const on = (p: XY, q: XY, r: XY) => orient(p, q, r) === 0 && Math.min(p.x, q.x) <= r.x && r.x <= Math.max(p.x, q.x) && Math.min(p.y, q.y) <= r.y && r.y <= Math.max(p.y, q.y);
  return on(a, b, c) || on(a, b, d) || on(c, d, a) || on(c, d, b);
}

/**
 * Whether the axis-aligned rectangle and the ring share any ground: a polygon vertex inside the
 * rectangle, a rectangle corner inside the polygon, or a polygon edge crossing a rectangle edge.
 */
export function rectTouches(poly: XY[], r: Rect): boolean {
  if (poly.some((p) => p.x >= r.x0 && p.x <= r.x1 && p.y >= r.y0 && p.y <= r.y1)) return true;
  const corners: XY[] = [
    { x: r.x0, y: r.y0 },
    { x: r.x1, y: r.y0 },
    { x: r.x1, y: r.y1 },
    { x: r.x0, y: r.y1 },
  ];
  if (corners.some((c) => pointInPolygon(poly, c))) return true;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    for (let k = 0; k < 4; k++) if (segmentsIntersect(a, b, corners[k], corners[(k + 1) % 4])) return true;
  }
  return false;
}

/**
 * Horizontal extent of the ring within the strip `y0 ≤ y ≤ y1`: the extreme x of a polygon cut by
 * two horizontal lines sits at a vertex inside the strip or at a crossing on one of its edges.
 * `null` when the strip misses the ring.
 */
export function stripExtent(poly: XY[], y0: number, y1: number): [number, number] | null {
  const xs = [...crossings(poly, y0), ...crossings(poly, y1), ...poly.filter((p) => p.y >= y0 && p.y <= y1).map((p) => p.x)];
  return xs.length ? [Math.min(...xs), Math.max(...xs)] : null;
}

/** x of every crossing of the horizontal line `y` with the ring, ascending. Half-open edges, so a vertex on the line counts once per side. */
export function crossings(poly: XY[], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (a.y > y !== b.y > y) xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
  }
  return xs.sort((p, q) => p - q);
}
